export type TourPosition = 'top' | 'bottom' | 'left' | 'right' | 'center';

export type TourBeforeAction = 'expand-settings-menu' | 'expand-help-menu';

export interface TourStep {
  id: string;
  titleKey: string;
  descriptionKey: string;
  targetElementId: string;
  fallbackTargetElementId?: string;
  targetSelector?: string;
  additionalTargetIds?: string[];
  route?: string;
  position: TourPosition;
  highlightPadding?: number;
  beforeAction?: TourBeforeAction;
}

export const MOBILE_ORIGINATION_TOUR_STEPS: TourStep[] = [
  {
    id: 'origination-my-applications',
    titleKey: 'tourStepOriginationMyApplicationsTitle',
    descriptionKey: 'tourStepOriginationMyApplicationsDescription',
    targetElementId: 'my-applications-header',
    route: '/dashboard',
    position: 'bottom',
    highlightPadding: 16,
  },
  {
    id: 'origination-my-tasks',
    titleKey: 'tourStepOriginationMyTasksTitle',
    descriptionKey: 'tourStepOriginationMyTasksDescription',
    targetElementId: 'tasks-panel-container',
    route: '/origination/:loanId',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'origination-loan-team',
    titleKey: 'tourStepOriginationLoanTeamTitle',
    descriptionKey: 'tourStepOriginationLoanTeamDescription',
    targetElementId: 'lo-bubble',
    route: '/origination/:loanId',
    position: 'left',
    highlightPadding: 8,
  },
  {
    id: 'origination-messages',
    titleKey: 'tourStepOriginationMessagesTitle',
    descriptionKey: 'tourStepOriginationMessagesDescription',
    targetElementId: 'origination-mobile-pill-messages',
    route: '/origination/:loanId',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'origination-loan-details',
    titleKey: 'tourStepOriginationLoanDetailsTitle',
    descriptionKey: 'tourStepOriginationLoanDetailsDescription',
    targetElementId: 'origination-mobile-pill-loan-details',
    route: '/origination/:loanId',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'origination-new-application',
    titleKey: 'tourStepOriginationNewApplicationTitle',
    descriptionKey: 'tourStepOriginationNewApplicationDescription',
    targetElementId: 'mobile-nav-products',
    route: '/dashboard',
    position: 'top',
    highlightPadding: 8,
  },
];

export const ORIGINATION_TOUR_STEPS: TourStep[] = [
  {
    id: 'origination-my-applications',
    titleKey: 'tourStepOriginationMyApplicationsTitle',
    descriptionKey: 'tourStepOriginationMyApplicationsDescription',
    targetElementId: 'my-applications-header',
    route: '/dashboard',
    position: 'bottom',
    highlightPadding: 16,
  },
  {
    id: 'origination-my-tasks',
    titleKey: 'tourStepOriginationMyTasksTitle',
    descriptionKey: 'tourStepOriginationMyTasksDescription',
    targetElementId: 'tasks-panel-container',
    route: '/origination/:loanId',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'origination-loan-team',
    titleKey: 'tourStepOriginationLoanTeamTitle',
    descriptionKey: 'tourStepOriginationLoanTeamDescription',
    targetElementId: 'loan-team-sidebar',
    route: '/origination/:loanId',
    position: 'left',
    highlightPadding: 8,
  },
  {
    id: 'origination-messages',
    titleKey: 'tourStepOriginationMessagesTitle',
    descriptionKey: 'tourStepOriginationMessagesDescription',
    targetElementId: 'origination-tab-messages',
    additionalTargetIds: ['origination-mobile-tab-messages'],
    route: '/origination/:loanId',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'origination-loan-details',
    titleKey: 'tourStepOriginationLoanDetailsTitle',
    descriptionKey: 'tourStepOriginationLoanDetailsDescription',
    targetElementId: 'origination-tab-loan-details',
    additionalTargetIds: ['origination-mobile-tab-loan-details'],
    route: '/origination/:loanId',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'origination-contact-support',
    titleKey: 'tourStepOriginationContactSupportTitle',
    descriptionKey: 'tourStepOriginationContactSupportDescription',
    targetElementId: 'global-header-phone',
    route: '/origination/:loanId',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'origination-new-application',
    titleKey: 'tourStepOriginationNewApplicationTitle',
    descriptionKey: 'tourStepOriginationNewApplicationDescription',
    targetElementId: 'my-accounts-products-grid',
    fallbackTargetElementId: 'products-section',
    route: '/dashboard',
    position: 'bottom',
    highlightPadding: 8,
  },
];

const SERVICING_MY_LOANS_TILE_STEP: TourStep = {
  id: 'my-loans-tile',
  titleKey: 'tourStepMyLoansTileTitle',
  descriptionKey: 'tourStepMyLoansTileDescription',
  targetElementId: 'account-card-0',
  route: '/dashboard',
  position: 'bottom',
  highlightPadding: 16,
};

const SERVICING_COMPLETED_APPLICATIONS_STEP: TourStep = {
  id: 'completed-applications',
  titleKey: 'tourStepCompletedApplicationsTitle',
  descriptionKey: 'tourStepCompletedApplicationsDescription',
  targetElementId: 'past-applications-header',
  route: '/dashboard',
  position: 'bottom',
  highlightPadding: 8,
};

const SERVICING_LINK_LOAN_STEP: TourStep = {
  id: 'link-loan',
  titleKey: 'tourStepLinkLoanTitle',
  descriptionKey: 'tourStepLinkLoanDescription',
  targetElementId: 'link-new-loan-button',
  additionalTargetIds: ['link-new-loan-button-mobile'],
  route: '/dashboard',
  position: 'bottom',
  highlightPadding: 8,
};

const DESKTOP_TOUR_STEPS_AFTER_DASHBOARD: TourStep[] = [
  {
    id: 'view-details-button',
    titleKey: 'tourStepViewDetailsTitle',
    descriptionKey: 'tourStepViewDetailsDescription',
    targetElementId: 'view-details-btn-0',
    route: '/dashboard',
    position: 'top',
    highlightPadding: 8,
  },
  {
    id: 'payments-make-payment',
    titleKey: 'tourStepMakePaymentTitle',
    descriptionKey: 'tourStepMakePaymentDescription',
    targetElementId: 'make-payment-button',
    route: '/loans/:accountId/details',
    position: 'left',
    highlightPadding: 8,
  },
  {
    id: 'payments-manage-autopay',
    titleKey: 'tourStepManageAutopayTitle',
    descriptionKey: 'tourStepManageAutopayDescription',
    targetElementId: 'manage-autopay-button',
    fallbackTargetElementId: 'manage-autopay-section',
    route: '/loans/:accountId/details',
    position: 'left',
    highlightPadding: 8,
  },
  {
    id: 'loan-settings',
    titleKey: 'tourStepLoanSettingsTitle',
    descriptionKey: 'tourStepLoanSettingsDescription',
    targetElementId: '',
    targetSelector: '.settings-dropdown-menu',
    route: '/loans/:accountId/details',
    position: 'left',
    highlightPadding: 8,
    beforeAction: 'expand-settings-menu',
  },
  {
    id: 'statements-documents',
    titleKey: 'tourStepStatementsDocsTitle',
    descriptionKey: 'tourStepStatementsDocsDescription',
    targetElementId: 'nav-tab-statements',
    route: '/loans/:accountId/details',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'help',
    titleKey: 'tourStepHelpTitle',
    descriptionKey: 'tourStepHelpDescription',
    targetElementId: '',
    targetSelector: '.help-resources-dropdown-menu',
    route: '/loans/:accountId/details',
    position: 'left',
    highlightPadding: 8,
    beforeAction: 'expand-help-menu',
  },
  {
    id: 'messages',
    titleKey: 'tourStepMessagesTitle',
    descriptionKey: 'tourStepMessagesDescription',
    targetElementId: 'messages-btn',
    route: '/loans/:accountId/details',
    position: 'bottom',
    highlightPadding: 8,
  },
];

/**
 * Builds the desktop servicing tour steps. The "Completed Applications" and
 * "Link My Loan" cards are only included when their corresponding elements are
 * actually rendered on the dashboard.
 */
export function buildDesktopTourSteps(hasPastApplications = false, hasLinkableLoan = false): TourStep[] {
  const steps: TourStep[] = [SERVICING_MY_LOANS_TILE_STEP];
  if (hasPastApplications) {
    steps.push(SERVICING_COMPLETED_APPLICATIONS_STEP);
  }
  if (hasLinkableLoan) {
    steps.push(SERVICING_LINK_LOAN_STEP);
  }
  steps.push(...DESKTOP_TOUR_STEPS_AFTER_DASHBOARD);
  return steps;
}

export const TOUR_STEPS: TourStep[] = buildDesktopTourSteps(true, true);

const MOBILE_TOUR_STEPS_BASE: TourStep[] = [
  {
    id: 'my-loans-tile',
    titleKey: 'tourStepMyLoansTileTitle',
    descriptionKey: 'tourStepMyLoansTileDescription',
    targetElementId: 'account-card-0',
    route: '/dashboard',
    position: 'bottom',
    highlightPadding: 16,
  },
  {
    id: 'view-details-button',
    titleKey: 'tourStepViewDetailsTitle',
    descriptionKey: 'tourStepViewDetailsDescription',
    targetElementId: 'view-details-btn-0',
    route: '/dashboard',
    position: 'top',
    highlightPadding: 8,
  },
  {
    id: 'payments-make-payment',
    titleKey: 'tourStepMakePaymentTitle',
    descriptionKey: 'tourStepMakePaymentDescription',
    targetElementId: 'make-payment-button',
    route: '/loans/:accountId/details',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'payments-manage-autopay',
    titleKey: 'tourStepManageAutopayTitle',
    descriptionKey: 'tourStepManageAutopayDescription',
    targetElementId: 'manage-autopay-button',
    fallbackTargetElementId: 'manage-autopay-section',
    route: '/loans/:accountId/details',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'loan-settings',
    titleKey: 'tourStepLoanSettingsTitle',
    descriptionKey: 'tourStepLoanSettingsDescription',
    targetElementId: 'native-tab-settings',
    route: '/loans/:accountId/details',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'statements-documents',
    titleKey: 'tourStepStatementsDocsTitle',
    descriptionKey: 'tourStepStatementsDocsDescription',
    targetElementId: 'native-tab-statements',
    route: '/loans/:accountId/details',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'help',
    titleKey: 'tourStepHelpTitle',
    descriptionKey: 'tourStepHelpDescription',
    targetElementId: 'native-help-btn',
    additionalTargetIds: ['mobile-sub-header-help-btn'],
    route: '/loans/:accountId/details',
    position: 'bottom',
    highlightPadding: 8,
  },
  {
    id: 'messages',
    titleKey: 'tourStepMessagesTitle',
    descriptionKey: 'tourStepMessagesDescription',
    targetElementId: 'native-messages-btn',
    additionalTargetIds: ['mobile-sub-header-messages-btn'],
    route: '/loans/:accountId/details',
    position: 'bottom',
    highlightPadding: 8,
  },
];

const MOBILE_COMPLETED_APPLICATIONS_STEP: TourStep = {
  id: 'completed-applications',
  titleKey: 'tourStepCompletedApplicationsTitle',
  descriptionKey: 'tourStepCompletedApplicationsDescription',
  targetElementId: 'past-applications-header',
  route: '/dashboard',
  position: 'bottom',
  highlightPadding: 16,
};

const MOBILE_LINK_LOAN_STEP: TourStep = {
  id: 'link-heloc',
  titleKey: 'tourStepLinkHelocTitle',
  descriptionKey: 'tourStepLinkHelocDescription',
  targetElementId: 'link-new-loan-button-mobile',
  route: '/dashboard',
  position: 'bottom',
  highlightPadding: 8,
};

/**
 * Builds the mobile servicing tour steps. The "Completed Applications" and
 * "Link My Loan" cards are only included when their corresponding elements are
 * actually rendered on the dashboard.
 */
export function buildMobileTourSteps(hasPastApplications = false, hasLinkableLoan = false): TourStep[] {
  const [myLoansTile, ...rest] = MOBILE_TOUR_STEPS_BASE;
  const steps: TourStep[] = [myLoansTile];
  if (hasPastApplications) {
    steps.push(MOBILE_COMPLETED_APPLICATIONS_STEP);
  }
  if (hasLinkableLoan) {
    steps.push(MOBILE_LINK_LOAN_STEP);
  }
  steps.push(...rest);
  return steps;
}

export function getTourStepsForUser(hasLoans: boolean): TourStep[] {
  if (!hasLoans) {
    return [];
  }
  return TOUR_STEPS;
}

export function getOriginationTourSteps(hasApplications: boolean): TourStep[] {
  if (!hasApplications) {
    return [];
  }
  return ORIGINATION_TOUR_STEPS;
}

export function buildTourSteps(
  hasPendingOriginationApp: boolean,
  hasServicingLoans: boolean,
  isMobile = false,
  hasPastApplications = false,
  hasLinkableLoan = false,
): TourStep[] {
  const steps: TourStep[] = [];

  if (hasPendingOriginationApp) {
    steps.push(...(isMobile ? MOBILE_ORIGINATION_TOUR_STEPS : ORIGINATION_TOUR_STEPS));
  }

  if (hasServicingLoans) {
    if (isMobile) {
      steps.push(...buildMobileTourSteps(hasPastApplications, hasLinkableLoan));
    } else {
      steps.push(...buildDesktopTourSteps(hasPastApplications, hasLinkableLoan));
    }
  }

  return steps;
}
